import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Plus,
  X,
  Edit2,
  Trash2,
  RefreshCw,
  BedDouble,
  Users,
  Check,
  Upload,
  Image,
  CalendarRange,
  Wand2,
  AlertTriangle,
  Camera,
  Star,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { shrinkPhotos, splitOversized, readJson, PHOTO_MAX_MB } from "../utils/uploadPhotos";

const API     = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE_BASE = process.env.REACT_APP_BACKEND_URL || "";

const AMENITY_OPTIONS = [
  "AC",
  "WiFi",
  "Hot Water",
  "TV",
  "Geyser",
  "Wardrobe",
  "Balcony",
  "Private Bathroom",
  "Kitchen",
  "Mini Fridge",
  "Washing Machine",
  "Garden View",
  // Split from a single "Parking": a property with a narrow gate or no
  // compound may take two-wheelers only, and a guest arriving by car needs
  // to know that before booking, not on arrival. Rooms saved before the split
  // are converted by migration 004 — re-saving does NOT clear the old value,
  // and with "Parking" gone from this list there was no checkbox left to
  // untick it, so it showed alongside the new one on the public room page.
  "2-Wheeler Parking",
  "4-Wheeler Parking",
  "Room Service",
  "Study Table",
];

function imgSrc(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BE_BASE}${url}`;
}

// Next N sequential room numbers, continuing from the highest existing
// numeric room number across every type (physical room numbers are
// usually unique property-wide, not per type). Always just a starting
// suggestion — every number stays freely editable before or after saving.
function suggestNextNumbers(rooms, count) {
  const nums = rooms
    .flatMap((r) => (r.units || []).map((u) => parseInt(u.roomNumber, 10)))
    .filter((n) => !Number.isNaN(n));
  const start = nums.length ? Math.max(...nums) + 1 : 1;
  return Array.from({ length: count }, (_, i) => String(start + i));
}

/* ── One physical room within a type being edited — number, an optional
   photo override, and remove, each applied immediately (no separate
   "save" step) via the dedicated unit endpoints. ── */
function UnitRow({ room, unit, authHeaders, onChanged, onRemoved }) {
  const [roomNumber, setRoomNumber] = useState(unit.roomNumber || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  async function saveNumber() {
    if (roomNumber === (unit.roomNumber || "")) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("roomNumber", roomNumber);
      const res = await fetch(`${API}/rooms/${room._id}/units/${unit._id}`, { method: "PATCH", headers: authHeaders(), body: fd });
      const json = await res.json();
      if (json.success) onChanged(json.data);
    } finally {
      setSaving(false);
    }
  }

  async function addPhoto(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSaving(true);
    try {
      const { ok, message } = splitOversized(await shrinkPhotos(files));
      if (message) window.alert(message);
      if (!ok.length) return;
      const fd = new FormData();
      fd.append("photos", JSON.stringify(unit.photos || []));
      ok.forEach(({ file }) => fd.append("photos", file));
      const res = await fetch(`${API}/rooms/${room._id}/units/${unit._id}`, { method: "PATCH", headers: authHeaders(), body: fd });
      const json = await readJson(res);
      if (json.success) onChanged(json.data);
      else window.alert(json.error || "Couldn't add that photo.");
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removeUnit() {
    if (!window.confirm(`Remove room ${unit.roomNumber || "(no number)"}? It won't be bookable afterward.`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/rooms/${room._id}/units/${unit._id}`, { method: "DELETE", headers: authHeaders() });
      const json = await res.json();
      if (json.success) onRemoved(unit._id);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
      <input
        value={roomNumber}
        onChange={(e) => setRoomNumber(e.target.value)}
        onBlur={saveNumber}
        placeholder="#"
        className="w-16 text-center font-bold border border-gray-200 rounded-lg px-2 py-1.5 text-sm
          focus:outline-none focus:ring-2 focus:ring-amber-400"
      />
      {unit.photos?.[0] ? (
        <img src={imgSrc(unit.photos[0])} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <span className="text-[10px] text-gray-400 flex-1 min-w-0">Uses the type's own photos</span>
      )}
      <button type="button" onClick={() => fileRef.current?.click()}
        title="Give this specific room its own photo"
        className="flex-shrink-0 w-11 h-11 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
        <Camera size={14} />
      </button>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} />
      <button type="button" onClick={removeUnit} className="flex-shrink-0 w-11 h-11 flex items-center justify-center text-red-400 hover:bg-red-50 rounded-lg">
        <Trash2 size={14} />
      </button>
      {saving && <RefreshCw size={12} className="animate-spin text-gray-400 flex-shrink-0" />}
    </div>
  );
}

/* ── Room-type create/edit form. Creating a type sets its quantity right
   there (a "Rooms" count generates that many editable number fields, no
   repeated per-room form); editing shows the live, individually-editable
   list of its physical rooms with an "+ Add room" to extend quantity. ── */
function RoomModal({ room, suggestedRoomNumber, onClose, onSaved, authHeaders }) {
  const isEdit = !!room?._id;
  const [form, setForm] = useState({
    name: room?.name || "",
    type: room?.type || "Room",
    description: room?.description || "",
    capacity: room?.capacity || 2,
    pricePerNight: room?.pricePerNight || 0,
    baseOccupancy: room?.baseOccupancy || room?.capacity || 2,
    extraPersonCharge: room?.extraPersonCharge || 0,
    minStay: room?.minStay || 1,
    amenities: room?.amenities || [],
  });
  // New-type quantity: purely local until the first save, since there's no
  // room._id yet to attach real unit records to.
  const [newUnitNumbers, setNewUnitNumbers] = useState(() => (isEdit ? [] : [suggestedRoomNumber || "1"]));
  // Existing type: the live unit list, kept in sync as UnitRow saves land.
  const [units, setUnits] = useState(room?.units || []);
  const [addingUnits, setAddingUnits] = useState(false);

  const [existingPhotos, setExistingPhotos] = useState(room?.photos || []);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [preparing, setPreparing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // For a brand-new type, Base Occupancy silently defaulting to a hardcoded
  // 2 regardless of Max Guests used to inflate the landing-page price with
  // a phantom extra-guest surcharge — Max Guests now keeps Base Occupancy
  // in sync until the admin explicitly edits Base Occupancy themselves.
  const [baseOccupancyTouched, setBaseOccupancyTouched] = useState(isEdit);

  function toggleAmenity(a) {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  }

  // Shrunk on selection (utils/uploadPhotos.js): small enough that ten fit
  // in one save, and previewed as data: URLs, which the live CSP allows.
  async function handlePhotoSelect(e) {
    const input = e.target;
    const files = Array.from(input.files || []).slice(0, Math.max(0, 10 - newPhotoFiles.length));
    if (!files.length) return;
    setPreparing(true);
    try {
      const { ok: ready, message } = splitOversized(await shrinkPhotos(files));
      setError(message);
      setNewPhotoFiles((prev) => [...prev, ...ready.map((r) => r.file)].slice(0, 10));
      setPreviews((prev) => [...prev, ...ready.map((r) => r.preview)].slice(0, 10));
    } finally {
      setPreparing(false);
      input.value = "";
    }
  }
  function removeExisting(i) { setExistingPhotos((prev) => prev.filter((_, j) => j !== i)); }
  // The photo at index 0 is the thumbnail everywhere this type's photos
  // are shown (landing page room card, admin room card, room detail
  // gallery hero) — no separate thumbnail field, just array order.
  function setThumbnail(i) {
    setExistingPhotos((prev) => {
      if (i === 0) return prev;
      const next = [...prev];
      const [chosen] = next.splice(i, 1);
      next.unshift(chosen);
      return next;
    });
  }
  function removeNew(i) {
    setPreviews((prev) => prev.filter((_, j) => j !== i));
    setNewPhotoFiles((prev) => prev.filter((_, j) => j !== i));
  }

  function addNewUnitField() {
    setNewUnitNumbers((prev) => {
      const nums = prev.map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n));
      const next = nums.length ? Math.max(...nums) + 1 : (parseInt(suggestedRoomNumber, 10) || 0) + prev.length + 1;
      return [...prev, String(next)];
    });
  }
  function updateNewUnitField(i, val) {
    setNewUnitNumbers((prev) => prev.map((n, j) => (j === i ? val : n)));
  }
  function removeNewUnitField(i) {
    setNewUnitNumbers((prev) => prev.filter((_, j) => j !== i));
  }

  async function submit() {
    if (!form.name.trim()) { setError("Room type name is required"); return; }
    if (!isEdit && newUnitNumbers.length === 0) { setError("Add at least one room."); return; }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("type", form.type);
      fd.append("description", form.description);
      fd.append("capacity", form.capacity);
      fd.append("pricePerNight", form.pricePerNight);
      fd.append("baseOccupancy", form.baseOccupancy);
      fd.append("extraPersonCharge", form.extraPersonCharge);
      fd.append("minStay", form.minStay);
      fd.append("amenities", JSON.stringify(form.amenities));
      fd.append("photos", JSON.stringify(existingPhotos));
      newPhotoFiles.forEach((f) => fd.append("photos", f));
      if (!isEdit) fd.append("units", JSON.stringify(newUnitNumbers));

      const url = isEdit ? `${API}/rooms/${room._id}` : `${API}/rooms`;
      const res = await fetch(url, { method: isEdit ? "PATCH" : "POST", headers: authHeaders(), body: fd });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.error);
      onSaved(json.data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function addRoomsToType(count) {
    setAddingUnits(true);
    try {
      const numbers = suggestNextNumbers([{ units }], count);
      const fd = new FormData();
      fd.append("roomNumbers", JSON.stringify(numbers));
      const res = await fetch(`${API}/rooms/${room._id}/units`, { method: "POST", headers: authHeaders(), body: fd });
      const json = await res.json();
      if (json.success) { setUnits(json.data.units); onSaved(json.data); }
    } finally {
      setAddingUnits(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-3xl shadow-2xl shadow-black/10 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-black text-gray-900">{isEdit ? "Edit Room Type" : "Add Room Type"}</h2>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-xl"><X size={16} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="room-name" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Room Type Name *
              </label>
              <input
                id="room-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Premium Room with Kitchen"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label htmlFor="room-type" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Category
              </label>
              <select
                id="room-type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              >
                {["Room", "Apartment", "Suite", "Dormitory"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="room-capacity" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Max Guests
              </label>
              <input
                id="room-capacity" type="number" min="1" value={form.capacity}
                onChange={(e) => {
                  const capacity = parseInt(e.target.value) || 1;
                  setForm((f) => ({ ...f, capacity, baseOccupancy: baseOccupancyTouched ? f.baseOccupancy : capacity }));
                }}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label htmlFor="room-price" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Price / Night (₹)
              </label>
              <input
                id="room-price" type="number" min="0" value={form.pricePerNight}
                onChange={(e) => setForm((f) => ({ ...f, pricePerNight: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="room-base-occupancy" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Base Occupancy
              </label>
              <input
                id="room-base-occupancy" type="number" min="1" value={form.baseOccupancy}
                onChange={(e) => { setBaseOccupancyTouched(true); setForm((f) => ({ ...f, baseOccupancy: parseInt(e.target.value) || 1 })); }}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <p className="text-[11px] text-gray-400 mt-1">Guests included in the price above</p>
            </div>
            <div>
              <label htmlFor="room-min-stay" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Minimum Stay (nights)
              </label>
              <input
                id="room-min-stay" type="number" min="1" value={form.minStay}
                onChange={(e) => setForm((f) => ({ ...f, minStay: Math.max(1, parseInt(e.target.value) || 1) }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              {/* Sent to the OTAs through the channel manager. Deliberately
                  NOT claimed for direct bookings — this page doesn't enforce
                  it there yet, and saying so would be a promise the booking
                  form doesn't keep. */}
              <p className="text-[11px] text-gray-400 mt-1">1 = no minimum. Sent to your connected OTAs.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="room-extra-charge" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Extra Person / Night (₹)
              </label>
              <input
                id="room-extra-charge" type="number" min="0" value={form.extraPersonCharge}
                onChange={(e) => setForm((f) => ({ ...f, extraPersonCharge: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <p className="text-[11px] text-gray-400 mt-1">Charged per guest beyond base occupancy</p>
            </div>
          </div>

          <div>
            <label htmlFor="room-description" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Description
            </label>
            <textarea
              id="room-description" rows={3} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Spacious room with king bed, AC, private bathroom…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Amenities</label>
            {/* The options list PLUS anything this room already has that is no
                longer on it. Rendering only AMENITY_OPTIONS meant retiring an
                option stranded its stored value: it kept showing on the public
                room page with no checkbox to untick it. That is exactly what
                happened when "Parking" became 2-/4-Wheeler (migration 004),
                and it would happen again the next time an option changes. */}
            <div className="flex flex-wrap gap-2">
              {[...AMENITY_OPTIONS, ...form.amenities.filter(a => !AMENITY_OPTIONS.includes(a))].map((a) => {
                const on = form.amenities.includes(a);
                return (
                  <button key={a} type="button" onClick={() => toggleAmenity(a)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all
                      ${on ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-gray-50 border-gray-200 text-gray-600 hover:border-amber-200"}`}>
                    {on && <Check size={10} className="inline mr-1" />}{a}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Photos <span className="text-gray-300 font-normal normal-case">(shared by every room of this type, unless one has its own override below)</span>
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl py-5
              cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-all group mb-3">
              <Upload size={24} className="text-gray-300 group-hover:text-amber-400 mb-1.5" />
              <span className="text-sm text-gray-400 group-hover:text-amber-600 font-medium">{preparing ? "Preparing photos…" : "Upload photos from your device"}</span>
              <span className="text-xs text-gray-300 mt-0.5">JPG, PNG · Up to 10 photos · {PHOTO_MAX_MB}MB each</span>
              <input type="file" multiple accept=".jpg,.jpeg,.png" className="hidden" onChange={handlePhotoSelect} />
            </label>
            {(existingPhotos.length > 0 || previews.length > 0) && (
              <div className="grid grid-cols-3 gap-2">
                {existingPhotos.map((p, i) => (
                  <div key={`ex-${i}`} className={`relative group rounded-xl overflow-hidden h-24 ${i === 0 ? "ring-2 ring-amber-400" : ""}`}>
                    <img src={imgSrc(p)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all" />
                    {/* Always at least partly visible (not opacity-0-until-hover)
                        — a touch device has no hover state, so a fully hidden
                        button here would be both invisible and undiscoverable
                        on phones/tablets, not just unstyled. */}
                    <button type="button" onClick={() => setThumbnail(i)}
                      title={i === 0 ? "Thumbnail" : "Set as thumbnail"}
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center transition-opacity
                        ${i === 0 ? "bg-amber-500 opacity-100" : "bg-black/60 opacity-80 group-hover:opacity-100 hover:bg-amber-500"}`}>
                      <Star size={12} className="text-white" fill={i === 0 ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => removeExisting(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">×</button>
                    <span className={`absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full ${i === 0 ? "bg-amber-500 text-white" : "bg-black/50 text-white"}`}>
                      {i === 0 ? "Thumbnail" : "Saved"}
                    </span>
                  </div>
                ))}
                {previews.map((p, i) => (
                  <div key={`new-${i}`} className="relative group rounded-xl overflow-hidden h-24">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all" />
                    <button onClick={() => removeNew(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">×</button>
                    <span className="absolute bottom-1 left-1 text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full">New</span>
                  </div>
                ))}
              </div>
            )}
            {existingPhotos.length > 1 && (
              <p className="text-[11px] text-gray-400 mt-2">
                <Star size={10} className="inline mr-0.5 -mt-0.5 text-amber-500" fill="currentColor" /> Thumbnail — shown on the landing page and room list. Click the star on any photo to make it the thumbnail.
              </p>
            )}
            {previews.length > 0 && (
              <p className="text-[11px] text-gray-400 mt-2">Newly added photos save first — reopen this room afterward to set one of them as the thumbnail.</p>
            )}
          </div>

          {/* Rooms / quantity */}
          <div className="border-t border-gray-100 pt-5">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              {isEdit ? `Rooms (${units.length})` : "Rooms"}
            </label>
            {!isEdit && (
              <>
                <div className="space-y-2 mb-2">
                  {newUnitNumbers.map((num, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={num} onChange={(e) => updateNewUnitField(i, e.target.value)}
                        placeholder="Room number"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                      {newUnitNumbers.length > 1 && (
                        <button type="button" onClick={() => removeNewUnitField(i)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg flex-shrink-0">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addNewUnitField}
                  className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1">
                  <Plus size={13} /> Add another room
                </button>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  One save creates all {newUnitNumbers.length} identical room{newUnitNumbers.length !== 1 ? "s" : ""} — same price, photos and amenities. Numbers are auto-suggested but freely editable.
                </p>
              </>
            )}
            {isEdit && (
              <>
                <div className="space-y-2 mb-2">
                  {units.map((u) => (
                    <UnitRow key={u._id} room={room} unit={u} authHeaders={authHeaders}
                      onChanged={(updatedRoom) => { setUnits(updatedRoom.units); onSaved(updatedRoom); }}
                      onRemoved={(unitId) => setUnits((prev) => prev.filter((x) => x._id !== unitId))} />
                  ))}
                  {units.length === 0 && <p className="text-sm text-gray-400 text-center py-3">No rooms left of this type.</p>}
                </div>
                <button type="button" onClick={() => addRoomsToType(1)} disabled={addingUnits}
                  className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 disabled:opacity-50">
                  <Plus size={13} /> {addingUnits ? "Adding…" : "Add another room"}
                </button>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Each row saves itself immediately — number and photo changes, and removing a room, all apply right away.
                </p>
              </>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>

        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t border-gray-100 pt-4">
          <button onClick={onClose} className="flex-1 py-3 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || preparing}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold
              hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 shadow-md">
            {saving ? "Saving…" : preparing ? "Preparing photos…" : isEdit ? "Save Changes" : `Create Room Type${newUnitNumbers.length > 1 ? ` (${newUnitNumbers.length} rooms)` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Seasonal / date-range rate overrides — only affects nights inside
   the chosen range; every other night keeps the type's default price. ── */
// Inline "+ Add another date range" control shown under an existing season —
// this is how one season covers multiple non-contiguous date ranges at the
// same price/label instead of needing a whole separate season per range.
function AddDateRangeInline({ roomId, seasonId, authHeaders, onAdded }) {
  const [open, setOpen]           = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  async function submit() {
    setError("");
    if (!startDate || !endDate) { setError("Both dates are required."); return; }
    setSaving(true);
    try {
      const res  = await fetch(`${API}/rooms/${roomId}/rate-overrides/${seasonId}/date-ranges`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ startDate, endDate }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onAdded(json.data);
      setStartDate(""); setEndDate(""); setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="text-xs text-amber-600 font-medium hover:text-amber-700 mt-2">
        + Add another date range
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 bg-white border border-gray-200 rounded-xl p-2.5">
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs
            focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs
            focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)}
          className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600">
          Cancel
        </button>
        <button onClick={submit} disabled={saving}
          className="flex-1 py-1.5 rounded-lg bg-gray-800 text-white text-xs font-medium disabled:opacity-60">
          {saving ? "Adding…" : "Add"}
        </button>
      </div>
    </div>
  );
}

// Seasonal pricing — one or more named seasons (e.g. "Diwali Weekend",
// "Summer Peak"), each with its own price and one or more date ranges.
// Occupancy-based suggestions — owner-approved, never automatic (see
// backend/services/dynamicPricingService.js). Purely additive to the
// seasons list below: applying one just creates a normal rateOverride,
// so it shows up in "seasons" like any manually-added one afterward.
function SuggestedRatesPanel({ room, authHeaders, onApplied }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingKey, setApplyingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/rooms/${room._id}/rate-suggestions`, { headers: authHeaders() });
      const json = await res.json();
      setSuggestions(json.data?.dateRanges || []);
    } catch { setSuggestions([]); }
    setLoading(false);
    // eslint-disable-next-line
  }, [room._id]);

  useEffect(() => { load(); }, [load]);

  const fmt = d => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  async function apply(s, key) {
    setApplyingKey(key);
    try {
      const res = await fetch(`${API}/rooms/${room._id}/rate-suggestions/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ start: s.start, end: s.end, price: s.suggestedPrice, direction: s.direction }),
      });
      const json = await res.json();
      if (json.success) { onApplied(json.data); load(); }
    } catch {}
    setApplyingKey(null);
  }

  if (loading) return null;
  if (suggestions.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
      <p className="text-xs font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
        <Wand2 size={13} /> Suggested Rates (next 30 days)
      </p>
      {suggestions.map((s, i) => {
        const key = `${s.start}-${s.end}`;
        const Icon = s.direction === "up" ? TrendingUp : TrendingDown;
        return (
          <div key={key} className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800 flex items-center gap-1.5">
                <Icon size={12} className={s.direction === "up" ? "text-emerald-600" : "text-blue-600"} />
                {fmt(s.start)} – {fmt(s.end)}
                <span className="text-gray-400">· {s.occupancyPct}% booked</span>
              </p>
              <p className="text-xs text-gray-500">₹{s.currentPrice.toLocaleString("en-IN")} → <span className="font-bold text-gray-800">₹{s.suggestedPrice.toLocaleString("en-IN")}</span></p>
            </div>
            <button onClick={() => apply(s, key)} disabled={applyingKey === key}
              className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {applyingKey === key ? "Applying…" : "Apply"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function RateOverridesModal({ room, onClose, onSaved, authHeaders }) {
  const [seasons, setSeasons]     = useState(room.rateOverrides || []);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");
  const [price, setPrice]         = useState("");
  const [label, setLabel]         = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  // Which days in the span the price applies to. Empty = every day, which is
  // exactly the old behaviour, so the default path is unchanged.
  const [weekdays, setWeekdays]   = useState([]);

  function toggleWeekday(n) {
    setWeekdays(w => (w.includes(n) ? w.filter(x => x !== n) : [...w, n].sort()));
  }

  function applyUpdatedRoom(updatedRoom) {
    setSeasons(updatedRoom.rateOverrides || []);
    onSaved(updatedRoom);
  }

  async function addSeason() {
    setError("");
    if (!startDate || !endDate || !price) {
      setError("Start date, end date, and price are required.");
      return;
    }
    setSaving(true);
    try {
      // The bulk route understands a weekday filter and expands the span into
      // the runs that match; the original route stays the path for a plain
      // every-day range, so existing behaviour is byte-for-byte unchanged.
      const useBulk = weekdays.length > 0;
      const res  = await fetch(
        `${API}/rooms/${room._id}/rate-overrides${useBulk ? "/bulk" : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          startDate, endDate, price: parseFloat(price), label,
          ...(useBulk ? { weekdays } : {}),
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      applyUpdatedRoom(useBulk ? json.data.room : json.data);
      setStartDate(""); setEndDate(""); setPrice(""); setLabel(""); setWeekdays([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeSeason(seasonId) {
    try {
      const res  = await fetch(`${API}/rooms/${room._id}/rate-overrides/${seasonId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!json.success) return;
      applyUpdatedRoom(json.data);
    } catch {}
  }

  async function removeDateRange(seasonId, rangeId) {
    try {
      const res  = await fetch(`${API}/rooms/${room._id}/rate-overrides/${seasonId}/date-ranges/${rangeId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!json.success) return;
      applyUpdatedRoom(json.data);
    } catch {}
  }

  const fmt = d => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <CalendarRange size={16} className="text-gray-500" /> Seasonal Pricing — {room.name}
          </h3>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-400 leading-relaxed">
            Define one or more named seasons (e.g. "Diwali Weekend", "Summer Peak"), each with its own
            price and one or more date ranges. Every other date keeps using this type's default price
            of ₹{(room.pricePerNight || 0).toLocaleString("en-IN")}/night.
          </p>

          <SuggestedRatesPanel room={{ ...room, rateOverrides: seasons }} authHeaders={authHeaders} onApplied={applyUpdatedRoom} />

          {seasons.length > 0 ? (
            <div className="space-y-3">
              {seasons.map((season) => (
                <div key={season._id} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">
                        {season.label || "Untitled season"}
                      </p>
                      <p className="text-xs text-amber-700 font-semibold">
                        ₹{season.price.toLocaleString("en-IN")}/night
                      </p>
                    </div>
                    <button onClick={() => removeSeason(season._id)}
                      title="Delete entire season"
                      className="w-11 h-11 flex items-center justify-center hover:bg-red-50 rounded-lg flex-shrink-0">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    {[...season.dateRanges]
                      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                      .map((r) => (
                        <div key={r._id} className="flex items-center justify-between gap-2
                          bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                          <span className="text-xs text-gray-700">{fmt(r.startDate)} – {fmt(r.endDate)}</span>
                          <button onClick={() => removeDateRange(season._id, r._id)}
                            className="p-1 hover:bg-red-50 rounded-md flex-shrink-0">
                            <X size={12} className="text-red-400" />
                          </button>
                        </div>
                      ))}
                  </div>

                  <AddDateRangeInline roomId={room._id} seasonId={season._id}
                    authHeaders={authHeaders} onAdded={applyUpdatedRoom} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No seasons yet</p>
          )}

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Add New Season</p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Label</label>
              <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Diwali weekend"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Price per Night (₹)</label>
              <input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 3500"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
            </div>
            {/* Which days in the span the price applies to. Nothing selected
                means every day — the original behaviour — so this only ever
                adds capability. "Weekends" is one tap because that is the
                case people actually price differently. */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500">Apply to</label>
                <button type="button"
                  onClick={() => setWeekdays(w => (w.length === 2 && w.includes(0) && w.includes(6) ? [] : [0, 6]))}
                  className="text-[11px] font-medium text-amber-600 hover:text-amber-700">
                  {weekdays.length === 2 && weekdays.includes(0) && weekdays.includes(6)
                    ? "Clear" : "Weekends only"}
                </button>
              </div>
              <div className="flex gap-1">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => {
                  const on = weekdays.includes(i);
                  return (
                    <button key={i} type="button" onClick={() => toggleWeekday(i)}
                      aria-label={["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][i]}
                      aria-pressed={on}
                      className={`flex-1 min-h-[38px] rounded-lg text-xs font-semibold border transition-colors
                        ${on ? "bg-amber-500 border-amber-500 text-white"
                             : "bg-gray-50 border-gray-200 text-gray-500 hover:border-amber-200"}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {weekdays.length === 0
                  ? "Every day in the range."
                  : `Only the selected days between those dates.`}
              </p>
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
            <button onClick={addSeason} disabled={saving}
              className="w-full py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium
                hover:bg-gray-900 disabled:opacity-60">
              {saving ? "Adding…" : "Add Season"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// One-time, admin-triggered consolidation of rooms saved before `units[]`
// existed — either a real single physical room, or several near-duplicate
// documents that used to represent one type. Safe to click more than once
// (already-migrated types are skipped). The old per-room documents are
// soft-deleted, not destroyed, so historical bookings still resolve.
function MigrateLegacyRooms({ legacyCount, authHeaders, onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  if (legacyCount === 0) return null;

  async function migrate() {
    setRunning(true);
    try {
      const res = await fetch(`${API}/rooms/migrate-to-units`, { method: "POST", headers: authHeaders() });
      const json = await res.json();
      if (json.success) { setResult(json.migrated); onDone(); }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="bg-indigo-50 border-2 border-indigo-300 rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
        <Wand2 size={20} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-indigo-800">
          {legacyCount} room{legacyCount !== 1 ? "s" : ""} need one-time cleanup
        </p>
        <p className="text-sm text-indigo-700 mt-1">
          These were saved before room types had a proper room list — this consolidates same-named rooms into one
          type with its rooms listed inside it, matching everything below. Old records are deactivated, not deleted.
        </p>
        <p className="text-xs text-indigo-500 mt-1.5">
          Note: bookings made before this runs won't be reflected in the new per-room availability count — double-check the calendar for any real upcoming stays afterward.
        </p>
        <button onClick={migrate} disabled={running}
          className="mt-3 text-xs font-bold text-white bg-indigo-500 hover:bg-indigo-600 px-3 py-2 rounded-xl disabled:opacity-60">
          {running ? "Migrating…" : `Migrate ${legacyCount} room${legacyCount !== 1 ? "s" : ""}`}
        </button>
        {result !== null && <p className="text-xs font-semibold text-indigo-700 mt-2">Created {result} room type{result !== 1 ? "s" : ""}.</p>}
      </div>
    </div>
  );
}

// Flags any active room type with a unit that has no room number set —
// staff can't tell same-type rooms apart on the calendar without one.
function MissingRoomNumbers({ rooms, authHeaders, onDone }) {
  const candidates = useMemo(
    () => rooms.flatMap((r) => (r.units || []).filter((u) => u.active !== false && !u.roomNumber).map((u) => ({ room: r, unit: u }))),
    [rooms]
  );
  const [applying, setApplying] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (candidates.length === 0 || dismissed) return null;

  async function autoNumber() {
    setApplying(true);
    try {
      let next = (() => {
        const nums = rooms.flatMap((r) => (r.units || []).map((u) => parseInt(u.roomNumber, 10))).filter((n) => !Number.isNaN(n));
        return nums.length ? Math.max(...nums) + 1 : 1;
      })();
      for (const { room, unit } of candidates) {
        const fd = new FormData();
        fd.append("roomNumber", String(next));
        await fetch(`${API}/rooms/${room._id}/units/${unit._id}`, { method: "PATCH", headers: authHeaders(), body: fd });
        next += 1;
      }
      onDone();
    } catch {}
    setApplying(false);
  }

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
        <AlertTriangle size={20} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-amber-800">
          {candidates.length} room{candidates.length !== 1 ? "s" : ""} have no room number set
        </p>
        <p className="text-sm text-amber-700 mt-1">
          "{candidates[0].room.name}"{candidates.length > 1 ? ` and ${candidates.length - 1} more` : ""} — auto-fill assigns each the next free number in order; edit any of them afterward if it's wrong.
        </p>
        <div className="flex gap-2 mt-3">
          <button onClick={autoNumber} disabled={applying}
            className="text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 px-3 py-2 rounded-xl disabled:opacity-60">
            {applying ? "Numbering…" : `Auto-number ${candidates.length} room${candidates.length !== 1 ? "s" : ""}`}
          </button>
          <button onClick={() => setDismissed(true)} className="text-xs font-medium text-amber-700 hover:text-amber-900 px-3 py-2">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RoomsPage() {
  const { authHeaders } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "new" | room object
  const [deleteId, setDeleteId] = useState(null);
  const [rateModal, setRateModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/rooms`, { headers: authHeaders() });
      const json = await res.json();
      setRooms(json.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function deleteRoomType(id) {
    await fetch(`${API}/rooms/${id}`, { method: "DELETE", headers: authHeaders() });
    setRooms((prev) => prev.filter((r) => r._id !== id));
    setDeleteId(null);
  }

  function onSaved(room) {
    setRooms((prev) => {
      const idx = prev.findIndex((r) => r._id === room._id);
      if (idx >= 0) { const n = [...prev]; n[idx] = room; return n; }
      return [room, ...prev];
    });
  }

  // Rooms saved before `units[]` existed — shown via the migration banner
  // instead of the normal grid until consolidated, since they don't have
  // the shape the rest of this page (and the calendar/booking flow) expect.
  const legacyRooms = useMemo(() => rooms.filter((r) => !Array.isArray(r.units) || r.units.length === 0), [rooms]);
  const migratedRooms = useMemo(() => rooms.filter((r) => Array.isArray(r.units) && r.units.length > 0), [rooms]);
  const totalUnits = useMemo(() => migratedRooms.reduce((sum, r) => sum + r.units.filter((u) => u.active !== false).length, 0), [migratedRooms]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Rooms & Apartments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {totalUnits} room{totalUnits !== 1 ? "s" : ""} · {migratedRooms.length} type{migratedRooms.length !== 1 ? "s" : ""} ·
            <span className="text-amber-600 font-medium"> Room selection is mandatory for guest bookings</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setModal("new")}
            className="tap-44 flex items-center gap-2 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500
              px-4 py-2 rounded-xl shadow-md hover:from-amber-600 hover:to-orange-600 transition-all">
            <Plus size={15} /> Add Room Type
          </button>
        </div>
      </div>

      {!loading && (legacyRooms.length > 0 || migratedRooms.length > 0) && (
        <div className="space-y-4">
          <MigrateLegacyRooms legacyCount={legacyRooms.length} authHeaders={authHeaders} onDone={load} />
          <MissingRoomNumbers rooms={migratedRooms} authHeaders={authHeaders} onDone={load} />
        </div>
      )}

      {!loading && rooms.length === 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
            <BedDouble size={20} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-amber-800">No rooms configured yet!</p>
            <p className="text-sm text-amber-700 mt-1">
              Guests <strong>cannot make bookings</strong> until at least one room type is created.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw size={28} className="animate-spin text-amber-400" />
        </div>
      ) : migratedRooms.length === 0 && legacyRooms.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-16 text-center">
          <BedDouble size={56} className="text-gray-200 mx-auto mb-4" />
          <h3 className="font-black text-gray-900 text-lg mb-2">Add Your First Room Type</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
            Set a name, price and photos once, then say how many identical rooms you have.
          </p>
          <button onClick={() => setModal("new")}
            className="inline-flex items-center gap-2 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500
              px-6 py-3 rounded-2xl shadow-md">
            <Plus size={16} /> Add First Room Type
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {migratedRooms.map((room) => {
            const activeUnits = room.units.filter((u) => u.active !== false);
            return (
              <div key={room._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="relative h-40 overflow-hidden">
                  {room.photos?.[0] ? (
                    <img src={imgSrc(room.photos[0])} alt={room.name} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center gap-2">
                      <Image size={32} className="text-amber-300" />
                      <p className="text-xs text-amber-400">No photos yet</p>
                    </div>
                  )}
                  {/* opacity-80 (not 0) as the resting state — a touch
                      device has no hover, so these would otherwise be the
                      only way to edit/delete a room type and yet be
                      completely invisible and untappable on a phone. */}
                  <div className="absolute top-2 right-2 flex gap-3 sm:gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setRateModal(room)} title="Seasonal Pricing"
                      className="tap-44 w-8 h-8 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center shadow-sm hover:bg-white">
                      <CalendarRange size={14} className="text-gray-600" />
                    </button>
                    <button onClick={() => setModal(room)}
                      className="tap-44 w-8 h-8 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center shadow-sm hover:bg-white">
                      <Edit2 size={14} className="text-gray-600" />
                    </button>
                    <button onClick={() => setDeleteId(room._id)}
                      className="tap-44 w-8 h-8 bg-white/90 backdrop-blur rounded-lg flex items-center justify-center shadow-sm hover:bg-red-50">
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-1.5">
                    <h3 className="font-black text-gray-900 text-base">{room.name}</h3>
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ml-2">{room.type}</span>
                  </div>
                  {room.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">{room.description}</p>}
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="flex items-center gap-1 text-gray-500"><Users size={13} /> {room.capacity} guests</span>
                    <span className="font-black text-gray-900">
                      ₹{(room.pricePerNight || 0).toLocaleString("en-IN")}
                      <span className="text-gray-400 font-normal text-xs">/night</span>
                    </span>
                  </div>
                  {room.extraPersonCharge > 0 && (
                    <p className="text-[11px] text-gray-400 mb-2">
                      +₹{room.extraPersonCharge.toLocaleString("en-IN")}/night per guest beyond {room.baseOccupancy || room.capacity}
                    </p>
                  )}
                  {room.amenities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {room.amenities.slice(0, 5).map((a) => (
                        <span key={a} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{a}</span>
                      ))}
                      {room.amenities.length > 5 && <span className="text-[10px] text-gray-400">+{room.amenities.length - 5}</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                    <div className="flex flex-wrap gap-1">
                      {activeUnits.map((u) => (
                        <span key={u._id} className="w-6 h-6 rounded-md bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center" title={`Room ${u.roomNumber || "?"}`}>
                          {u.roomNumber || "?"}
                        </span>
                      ))}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">{activeUnits.length} room{activeUnits.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <RoomModal
          room={modal === "new" ? null : modal}
          suggestedRoomNumber={suggestNextNumbers(migratedRooms, 1)[0]}
          onClose={() => setModal(null)}
          onSaved={onSaved}
          authHeaders={authHeaders}
        />
      )}
      {rateModal && (
        <RateOverridesModal room={rateModal} onClose={() => setRateModal(null)} onSaved={onSaved} authHeaders={authHeaders} />
      )}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 p-6 w-80 text-center">
            <Trash2 size={28} className="text-red-500 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">Delete Room Type?</h3>
            <p className="text-sm text-gray-500 mb-5">Every room within it is removed too. Existing bookings won't be affected.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600">Cancel</button>
              <button onClick={() => deleteRoomType(deleteId)} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
